import type { IBuffer } from './../../models/types/planning.d';
import type IntlService from 'ember-intl/services/intl';
import Buffer from 'volta/models/buffer';

import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';

type TOrientation = 'vertical' | 'horizontal';
type TDimension = 'height' | 'width';

interface IArgs {
  orientation?: TOrientation;
  buffer: IBuffer;
  availableStock?: number;
  aseTitle?: string;
  onHandTooltip?: string;
}

export default class BvBuffer extends Component<IArgs> {
  @service intl!: IntlService;

  get orientation() {
    return this.args.orientation ?? 'vertical';
  }

  get isVertical() {
    return this.orientation === 'vertical';
  }

  get buffer() {
    return new Buffer(this.args.buffer);
  }

  get dimension(): TDimension {
    return this.orientation === 'vertical' ? 'height' : 'width';
  }

  get total() {
    const { availableStock = 0 } = this.args;
    return Math.max(availableStock, this.buffer.topOfGreen);
  }

  get hasBuffer() {
    const { buffer } = this;
    return buffer.redBase + buffer.redSafety + buffer.yellow + buffer.green > 0;
  }

  // Tooltips

  get otogTooltip() {
    const { availableStock } = this.args;
    return isPresent(availableStock)
      ? htmlSafe(
          `${this.intl.t('otog')} : ${this.intl.formatNumber(
            availableStock! - this.buffer.topOfGreen
          )}`
        )
      : '';
  }

  get greenTooltip() {
    return isPresent(this.buffer.topOfGreen)
      ? htmlSafe(`${this.intl.t('TOG')} : ${this.intl.formatNumber(this.buffer.topOfGreen)}`)
      : '';
  }

  get redBaseTooltip() {
    return isPresent(this.buffer.redBase)
      ? htmlSafe(`${this.intl.t('redBase')} : ${this.intl.formatNumber(this.buffer.redBase)}`)
      : '';
  }

  get redSafetyTooltip() {
    const tor = this.intl.formatNumber(this.buffer.topOfRed);
    return isPresent(tor) ? htmlSafe(`${this.intl.t('TOR')} : ${tor}`) : '';
  }

  get yellowTooltip() {
    const toy = this.intl.formatNumber(this.buffer.topOfYellow);
    return isPresent(toy) ? htmlSafe(`${this.intl.t('TOY')} : ${toy}`) : '';
  }

  get aseTitle() {
    return this.args.aseTitle ?? this.intl.t('netFlow');
  }

  get availableStockTooltip() {
    let prefix = this.args.onHandTooltip ?? this.aseTitle ?? '';
    let { availableStock } = this.args;
    return isPresent(availableStock)
      ? htmlSafe(`${prefix} : ${this.intl.formatNumber(availableStock!)}`)
      : '';
  }

  // Percentages
  //

  get redBasePct() {
    return BvBuffer.computePct(this.total, this.buffer.redBase);
  }

  get redSafetyPct() {
    return BvBuffer.computePct(this.total, this.buffer.redSafety);
  }

  get yellowPct() {
    return BvBuffer.computePct(this.total, this.buffer.yellow);
  }

  get greenPct() {
    return BvBuffer.computePct(this.total, this.buffer.green);
  }

  get availableStockPct() {
    return BvBuffer.computePct(this.total, this.args.availableStock ?? 0);
  }

  get otogPct() {
    const { availableStock = 0 } = this.args;
    return BvBuffer.computePct(this.total, availableStock - this.buffer.topOfGreen);
  }

  // Margins

  get otogLeft() {
    return this.redBasePct + this.redSafetyPct + this.yellowPct + this.greenPct;
  }

  get greenLeft() {
    return this.redBasePct + this.redSafetyPct + this.yellowPct;
  }

  get yellowLeft() {
    return this.redBasePct + this.redSafetyPct;
  }

  get redSafetyLeft() {
    return this.redBasePct;
  }

  get redBaseLeft() {
    return 0;
  }

  // Styles

  get otogStyle() {
    return BvBuffer.computeStyle(this.orientation, this.dimension, this.otogPct, this.otogLeft);
  }

  get greenStyle() {
    return BvBuffer.computeStyle(this.orientation, this.dimension, this.greenPct, this.greenLeft);
  }

  get yellowStyle() {
    return BvBuffer.computeStyle(this.orientation, this.dimension, this.yellowPct, this.yellowLeft);
  }

  get redBaseStyle() {
    return BvBuffer.computeStyle(
      this.orientation,
      this.dimension,
      this.redBasePct,
      this.redBaseLeft
    );
  }

  get redSafetyStyle() {
    return BvBuffer.computeStyle(
      this.orientation,
      this.dimension,
      this.redSafetyPct,
      this.redSafetyLeft
    );
  }

  get availableStockStyle() {
    return htmlSafe(
      `${this.orientation === 'horizontal' ? 'left' : 'bottom'}:${this.availableStockPct}%`
    );
  }

  get avgOnHandStyle() {
    return htmlSafe(
      `${this.orientation === 'horizontal' ? 'left' : 'bottom'}:${BvBuffer.computePct(
        this.total,
        this.buffer.avgOnHand
      )}%`
    );
  }

  get avgOnHandTooltip() {
    let prefix = this.intl.t('avgTargetOnHand');
    let { avgOnHand } = this.buffer;
    return isPresent(avgOnHand) ? htmlSafe(`${prefix} : ${this.intl.formatNumber(avgOnHand)}`) : '';
  }

  static computePct(total: number, value: number) {
    const pct = total === 0.0 ? 0.0 : value / total;
    return (pct < 0 ? 0 : pct) * 100.0;
  }

  static computeStyle(orientation: TOrientation, dimension: TDimension, pct: number, left: number) {
    return htmlSafe(
      `${dimension}:${pct}%;` + (orientation === 'horizontal' ? `left:${left}%` : '')
    );
  }
}
